import { api } from '@/lib/api';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { arr, classLabel, dOnly, dt, studentName, useClasses, useGet, type Col } from '@/features/shared/ops/kit';
import { saveBlob } from '@/lib/native';

export const opts = (rows: any[], label: (r: any) => string, value: (r: any) => string | number = (r) => r.id) => rows.map((r) => ({ value: value(r), label: label(r) }));

export function useClassOptions() {
  const q = useClasses();
  return { ...q, classes: arr(q.data), options: opts(arr(q.data), classLabel) };
}
export function useStaffOptions(role?: string) {
  const q = useGet<any>('staffpick', '/school/staff', { per_page: 100, role, status: 'active' });
  const rows = arr(q.data);
  return { ...q, staff: rows, options: opts(rows, (u) => u.name || [u.first_name, u.last_name].join(' ')) };
}
export function useSubjectOptions() {
  const q = useGet<any>('subjectpick', '/school/subjects');
  const rows = arr(q.data);
  return { ...q, subjects: rows, options: opts(rows, (s) => s.name) };
}
export function useLevelOptions() {
  const q = useGet<any>('levelpick', '/school/class-levels');
  const rows = arr(q.data);
  return { ...q, levels: rows, options: opts(rows, (l) => l.name) };
}

export const RELATIONSHIPS = ['father', 'mother', 'guardian', 'other'].map((v) => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }));
export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((l, i) => ({ value: i + 1, label: l }));
export const badge = (s?: string | null) => (s ? <StatusBadge status={String(s)} /> : <span className="text-slate-400">—</span>);
export const fullName = (p: any) => p?.name || [p?.first_name, p?.last_name].filter(Boolean).join(' ') || studentName(p);

/** Columns derived from the first row, for endpoints whose row shape is not fixed. */
export function autoCols(rows: any[], skip: string[] = []): Col<any>[] {
  const first = rows[0];
  if (!first || typeof first !== 'object') return [];
  return Object.keys(first)
    .filter((k) => !skip.includes(k) && (first[k] == null || typeof first[k] !== 'object'))
    .slice(0, 7)
    .map((k) => ({
      head: k.replace(/_/g, ' '),
      cell: (r: any) => {
        const v = r[k];
        if (v == null || v === '') return '—';
        if (typeof v === 'boolean') return v ? 'Yes' : 'No';
        if (/status/.test(k)) return badge(String(v));
        if (/_at$/.test(k)) return dt(String(v));
        if (/date|_on$/.test(k)) return dOnly(String(v));
        return String(v);
      },
    }));
}

/** Download a GET endpoint as a file (CSV exports). */
export async function downloadFile(url: string, params: Record<string, unknown>, name: string) {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null));
  const res = await api.get(url, { params: clean, responseType: 'blob' });
  await saveBlob(res.data as Blob, name);
}

export function formData(o: Record<string, any>) {
  const f = new FormData();
  Object.entries(o).forEach(([k, v]) => {
    if (v === '' || v == null) return;
    f.append(k, v instanceof Blob ? v : typeof v === 'boolean' ? (v ? '1' : '0') : String(v));
  });
  return f;
}
